/**
 * opponents.ts —— 电脑对手的昵称与风格描述（第 10 轮 R6）
 *
 * 口径红线（round10-requirements R6 / PRD 4.5）：
 *   - 昵称映射为 PM 定稿的权威表，不得改动；
 *   - 风格描述只用于「玩家主动点开」后的风格卡，默认不在牌桌上展示（防剧透）；
 *   - 本模块是**纯数据 + 纯函数**（无 DOM、无资源导入），因此可被状态层直接引用，
 *     由 controller 在开局时把昵称写进 `GameConfig.names`（随存档持久化、恢复后不丢）。
 *
 * 为什么昵称由 style 推导而不是随机命名：R6.5 要求「同座位昵称与其风格一致且同局稳定」，
 * 而风格（`styles`）本身已随存档持久化，故昵称是 style 的纯函数即天然稳定、可复现。
 */

import type { StyleKey } from '../core/ai';

/** 昵称映射（PM 定稿，权威，round10-requirements R6 / PRD 4.5 表）。 */
export const STYLE_NICKNAMES: Readonly<Record<StyleKey, string>> = {
  tag: '冷面狙击手',
  lag: '闪电风暴',
  rock: '磐石老僧',
  station: '跟注不倦',
  maniac: '诈唬狂人',
};

/** 一句话风格描述（点开风格卡后展示；DESIGN 4.5「5 种风格描述文案」）。 */
export const STYLE_DESCRIPTIONS: Readonly<Record<StyleKey, string>> = {
  tag: '入池少但入池即凶，稳健精算，偏爱在有利局面重拳出击。',
  lag: '入池多、高频加注，激进冒险，用压力逼你犯错。',
  rock: '只玩强牌、极度谨慎，遇加注就收，稳字当头。',
  station: '爱跟注不爱加注，被动黏人，跟到底看你有没有货。',
  maniac: '高频施压诈唬，张扬好斗，真真假假让你抓不准。',
};

/**
 * 按各座位分配的风格生成稳定、去重的昵称表。
 *
 *   - 每种风格首次出现用权威昵称本名；
 *   - 同风格多个对手用「昵称 + 序号」区分（如「诈唬狂人 2」），保证同局内不重名（R6.5）；
 *   - 座位按升序遍历，序号分配确定，故同一 styles 恒得同一昵称表（可复现、可续局）。
 *
 * @param styles 座位号 → 风格 key（人类座位不在其中）。
 * @returns 座位号 → 昵称。
 */
export function assignNicknames(styles: Readonly<Record<number, StyleKey>>): Record<number, string> {
  const seats = Object.keys(styles)
    .map(Number)
    .sort((a, b) => a - b);
  const usedByStyle = new Map<StyleKey, number>();
  const out: Record<number, string> = {};
  for (const seat of seats) {
    const key = styles[seat];
    const n = (usedByStyle.get(key) ?? 0) + 1;
    usedByStyle.set(key, n);
    const base = STYLE_NICKNAMES[key];
    out[seat] = n === 1 ? base : `${base} ${n}`;
  }
  return out;
}
